import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, Truck, EnvelopeSimple, Phone, MapPin } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrderConfirmationPage() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await axios.get(`${API}/orders/${orderId}`);
        setOrder(response.data);
      } catch (error) {
        console.error('Error fetching order:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [orderId]);

  if (loading) {
    return (
      <div className="max-w-[800px] mx-auto px-6 md:px-12 py-20 text-center">
        <div className="spinner mx-auto"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="max-w-[800px] mx-auto px-6 md:px-12 py-20 text-center">
        <h1 className="font-serif text-3xl text-nexa-charcoal mb-4">Order Not Found</h1>
        <Link to="/shop">
          <Button className="btn-primary">Continue Shopping</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-[800px] mx-auto px-6 md:px-12 py-12" data-testid="order-confirmation-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-12"
      >
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mb-6">
          <CheckCircle size={48} weight="fill" className="text-green-600" />
        </div>
        <h1 className="font-serif text-4xl font-medium text-nexa-charcoal mb-2">Thank You for Your Order!</h1>
        <p className="text-lg text-muted-foreground">Your order has been confirmed and is being processed.</p>
      </motion.div>

      <div className="bg-white rounded-lg border border-nexa-border p-6 mb-8" data-testid="order-details">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6 pb-6 border-b border-nexa-border">
          <div>
            <p className="text-sm text-muted-foreground">Order Number</p>
            <p className="font-serif text-xl font-semibold text-nexa-charcoal" data-testid="order-number">{order.order_number}</p>
          </div>
          <div className="flex items-center gap-2 bg-nexa-stone px-4 py-2 rounded-lg">
            <Truck size={20} className="text-nexa-gold" />
            <span className="text-sm font-medium text-nexa-charcoal">
              Estimated Delivery: {order.estimated_delivery}
            </span>
          </div>
        </div>

        {/* Order Items */}
        <div className="mb-6">
          <h3 className="font-semibold text-nexa-charcoal mb-4">Order Items</h3>
          <div className="space-y-4">
            {order.items.map((item, index) => (
              <div key={index} className="flex gap-4">
                <img 
                  src={item.image}
                  alt={item.name}
                  className="w-16 h-16 object-cover rounded"
                />
                <div className="flex-1">
                  <p className="font-medium text-nexa-charcoal">{item.name}</p>
                  <p className="text-sm text-muted-foreground">Qty: {item.quantity}</p>
                </div>
                <p className="font-medium text-nexa-charcoal">
                  ${(item.price * item.quantity).toFixed(2)}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Order Summary */}
        <div className="border-t border-nexa-border pt-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Subtotal</span>
            <span className="text-nexa-charcoal">${order.subtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Shipping</span>
            <span className="text-nexa-charcoal">
              {order.shipping_cost === 0 ? 'FREE' : `$${order.shipping_cost.toFixed(2)}`}
            </span>
          </div>
          <div className="flex justify-between pt-2 border-t border-nexa-border">
            <span className="font-semibold text-nexa-charcoal">Total</span>
            <span className="font-serif text-xl font-semibold text-nexa-charcoal">${order.total.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Shipping Address */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg border border-nexa-border p-6">
          <h3 className="font-semibold text-nexa-charcoal mb-4 flex items-center gap-2">
            <MapPin size={20} className="text-nexa-gold" />
            Shipping Address
          </h3>
          <p className="text-nexa-charcoal">
            {order.shipping_address.first_name} {order.shipping_address.last_name}
          </p>
          <p className="text-muted-foreground">{order.shipping_address.address}</p>
          <p className="text-muted-foreground">
            {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip_code}
          </p>
        </div>

        <div className="bg-white rounded-lg border border-nexa-border p-6">
          <h3 className="font-semibold text-nexa-charcoal mb-4">Payment Method</h3>
          <p className="text-nexa-charcoal capitalize">{order.payment_method === 'cod' ? 'Cash on Delivery' : 'Credit Card'}</p>
        </div>
      </div>

      {/* Customer Support */}
      <div className="bg-nexa-stone rounded-lg p-6 text-center">
        <h3 className="font-serif text-xl font-semibold text-nexa-charcoal mb-4">Need Help?</h3>
        <p className="text-muted-foreground mb-4">Our customer support team is here to assist you.</p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a href="mailto:support@nexaorbits.store" className="flex items-center gap-2 text-nexa-charcoal hover:text-nexa-gold transition-colors">
            <EnvelopeSimple size={18} />
            support@nexaorbits.store
          </a>
          <a href="tel:+18883511122" className="flex items-center gap-2 text-nexa-charcoal hover:text-nexa-gold transition-colors">
            <Phone size={18} />
            +1-888-351-1122
          </a>
        </div>
      </div>

      <div className="text-center mt-8">
        <Link to="/shop">
          <Button className="btn-primary" data-testid="continue-shopping-btn">Continue Shopping</Button>
        </Link>
      </div>
    </div>
  );
}
